import React, {ReactNode} from 'react';
import {Column, ColumnInstance, HeaderGroup, Row} from 'react-table';

import {RendererOf} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  analyticsCanBeHiddenColumnTypes,
  AnalyticsDataPoint,
  AnalyticsDataPointTypesEnum,
  AnalyticsTableMetricColumn,
  AnalyticsTableMetricColumnType,
  AnalyticsTableMetricRow,
  AnalyticsTableMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {renderFormattedMetricData} from '../../../helpers/analyticsFormatHelpers';
import {getMetricLabels} from '../../../helpers/metricLabels';
import {ReportElementIdsEnum} from '../../../helpers/reportHelpers/reportTypes';
import {TableHeadersEnum} from '../../table/tableCommons';
import {AnalyticsTooltip} from '../tooltips/analyticsTooltips';

export type TableData = {[metric: string]: AnalyticsDataPoint};

// This data passed in the columns as input will be usable by the output HeaderGroups and ColumnInstance. Using custom types so we can cast when needed.
type CustomTableData = {
  type: AnalyticsTableMetricColumnType;
  label: string;
  tooltip?: RendererOf<ReactNode>;
  from?: string;
};
export type CustomTableColumn<T extends object> = Column<T> & CustomTableData;
export type CustomTableHeaderGroup<T extends object> = HeaderGroup<T> & CustomTableData;
export type CustomTableColumnInstance<T extends object> = ColumnInstance<T> & CustomTableData;

// Useful constant since the teamColumnId is often filtered out
export const teamColumnId = 'team_id';
export const accountColumnId = 'account';

// Casting a ColumnInstance<T> column to CustomTableColumnInstance<T> as it's not inferred automatically.
export const isCustomTableColumn = <T extends object>(
  col: ColumnInstance<T>,
): col is CustomTableColumnInstance<T> => 'type' in col;

// Metrics Value to table cell mappers.
export const mapMetricRowToTableRow = (
  columns: ReadonlyArray<AnalyticsTableMetricColumn>,
  metricRow: AnalyticsTableMetricRow,
  blockedColumnIds?: string[],
): TableData =>
  columns.reduce((acc, column, i) => {
    if (blockedColumnIds && blockedColumnIds.includes(column.id)) {
      return acc;
    }
    return {
      ...acc,
      [column.id]: metricRow[i],
    };
  }, {});

// Some columns need a specific renderer.
const getColumnCustomDataPointType = (
  id: string,
  type: AnalyticsDataPointTypesEnum,
): AnalyticsDataPointTypesEnum => {
  switch (id) {
    case 'pct_active_segments':
    case 'csat_survey_satisfaction':
    case 'pct_csat_survey_satisfaction':
    case 'pct_sla_achieved':
    case 'deflection_rate':
    case 'drop_off_rate':
    case 'customer_reply_rate':
    case 'ai_participation_rate':
    case 'ai_answer_rate':
    case 'ai_success_rate':
    case 'ai_resolution_rate':
      return AnalyticsDataPointTypesEnum.PROGRESS;
    default:
      return type;
  }
};

function buildCellRenderer(columnId: string) {
  return function renderCell(cell: {value: AnalyticsDataPoint}) {
    // Extract the data point from the react-table cell object.
    const dataPoint = cell.value;
    // Handle custom per-column formatting.
    const dataPointType = getColumnCustomDataPointType(columnId, dataPoint.type);

    return <>{renderFormattedMetricData(dataPoint, dataPointType)}</>;
  };
}

export function mapMetricColumnsToTableColumns(
  metric: AnalyticsTableMetricsEnum,
  columns: ReadonlyArray<AnalyticsTableMetricColumn>,
  reportSectionId: ReportElementIdsEnum,
): Array<CustomTableColumn<TableData>> {
  const labels = getMetricLabels(metric);
  return columns.map((column, i) => {
    const defaultTooltip = column.tooltip ? {label: column.tooltip} : undefined;
    const columnLabels = labels?.columns?.[column.id];
    const tooltip = columnLabels?.tooltip ?? defaultTooltip;

    return {
      id: column.id,
      accessor: column.id,
      label: column.label,
      from: column.from,
      Header: columnLabels?.label ?? column.label,
      type: column.type,
      tooltip: (header) => (
        <AnalyticsTooltip tooltip={tooltip} reportSectionId={reportSectionId}>
          {header}
        </AnalyticsTooltip>
      ),
      index: i, // Needed for the sticky column behaviour to work.
      sortType: sortDataPoint,
      sortDescFirst: isColumnNumerical(column.type), // Columns should sort in descending order for numerical columns, ascending order for "text" columns.
      Cell: buildCellRenderer(column.id),
    };
  });
}

export const isColumnNumerical = (columnType: AnalyticsTableMetricColumnType) =>
  getCellHeaderType(columnType) === TableHeadersEnum.NUMERICAL;

const getCellHeaderType = (columnType: AnalyticsTableMetricColumnType) => {
  switch (columnType) {
    case AnalyticsDataPointTypesEnum.NUMERICAL:
    case AnalyticsDataPointTypesEnum.PERCENTAGE:
    case AnalyticsDataPointTypesEnum.PROGRESS:
    case AnalyticsDataPointTypesEnum.DURATION:
      return TableHeadersEnum.NUMERICAL;

    default:
      return TableHeadersEnum.TEXT;
  }
};

export const canColumnBeHidden = (columnType: AnalyticsTableMetricColumnType) =>
  analyticsCanBeHiddenColumnTypes.includes(columnType);

const sortDataPoint = (rowA: Row, rowB: Row, columnId: string, desc?: boolean) => {
  const valueA = rowA.values[columnId].value;
  const valueB = rowB.values[columnId].value;
  if (valueA === undefined || valueB === undefined) {
    return desc ? -1 : 1;
  }

  return valueA < valueB ? -1 : 1;
};
